import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, throwError } from 'rxjs';
import { AppErrors } from '@fitness-force/models';
import { CONST_URL_SEGEMENTS } from '@fitness-force/constants';
import {
  FormTemplateListItem,
  FormTemplateDetail,
  FormTemplateCreateRequest,
  FormTemplateUpdateRequest,
  FormTemplateListResponse,
  FormTemplateListParams,
} from '../models';
import { environment } from '@fitness-force/environments';

@Injectable({
  providedIn: 'root',
})
export class FormTemplateService {
  private readonly http = inject(HttpClient);
  private readonly API_BASE_URL = environment.BASE_URL;

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

  listTemplates(
    tenantId: string,
    params?: FormTemplateListParams,
  ): Observable<FormTemplateListResponse> {
    let httpParams = new HttpParams();
    if (params?.search) {
      httpParams = httpParams.set('search', params.search);
    }
    if (params?.folder_id !== undefined) {
      httpParams = httpParams.set('folder_id', params.folder_id.toString());
    }
    if (params?.is_active !== undefined) {
      httpParams = httpParams.set('is_active', params.is_active.toString());
    }

    return this.http.get<FormTemplateListResponse>(
      this.API_BASE_URL +
        CONST_URL_SEGEMENTS.SURVEY.LIST_FORM_TEMPLATES.replace(
          '{tenant_id}',
          tenantId,
        ),
      { headers: this.httpOptions.headers, params: httpParams }
    ).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('List Form Templates API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  getTemplate(
    tenantId: string,
    templateId: string,
  ): Observable<FormTemplateDetail> {
    return this.http.get<FormTemplateDetail>(
      this.API_BASE_URL +
        CONST_URL_SEGEMENTS.SURVEY.GET_FORM_TEMPLATE.replace(
          '{tenant_id}',
          tenantId,
        ).replace('{template_id}', templateId),
      { headers: this.httpOptions.headers }
    ).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Get Form Template API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  createTemplate(
    tenantId: string,
    payload: FormTemplateCreateRequest,
  ): Observable<{ id: string; message: string }> {
    return this.http.post<{ id: string; message: string }>(
      this.API_BASE_URL +
        CONST_URL_SEGEMENTS.SURVEY.CREATE_FORM_TEMPLATE.replace(
          '{tenant_id}',
          tenantId,
        ),
      payload,
      { headers: this.httpOptions.headers }
    ).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Create Form Template API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  updateTemplate(
    tenantId: string,
    templateId: string,
    payload: FormTemplateUpdateRequest,
  ): Observable<{ id: string; version: number; message: string }> {
    return this.http.put<{ id: string; version: number; message: string }>(
      this.API_BASE_URL +
        CONST_URL_SEGEMENTS.SURVEY.UPDATE_FORM_TEMPLATE.replace(
          '{tenant_id}',
          tenantId,
        ).replace('{template_id}', templateId),
      payload,
      { headers: this.httpOptions.headers }
    ).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Update Form Template API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  deleteTemplate(
    tenantId: string,
    templateId: string,
  ): Observable<{ id: string; message: string }> {
    return this.http.delete<{ id: string; message: string }>(
      this.API_BASE_URL +
        CONST_URL_SEGEMENTS.SURVEY.DELETE_FORM_TEMPLATE.replace(
          '{tenant_id}',
          tenantId,
        ).replace('{template_id}', templateId),
      { headers: this.httpOptions.headers }
    ).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Delete Form Template API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  markTemplateAsActive(
    tenantId: string,
    templateId: string,
  ): Observable<{ message: string }> {
    return this.http.put<{ message: string }>(
      this.API_BASE_URL +
        CONST_URL_SEGEMENTS.SURVEY.MARK_TEMPLATE_AS_ACTIVE.replace(
          '{tenant_id}',
          tenantId,
        ).replace('{template_id}', templateId),
      {},
      { headers: this.httpOptions.headers }
    ).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Mark Template as Active API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }

  markTemplateAsInactive(
    tenantId: string,
    templateId: string,
  ): Observable<{ message: string }> {
    return this.http.put<{ message: string }>(
      this.API_BASE_URL +
        CONST_URL_SEGEMENTS.SURVEY.MARK_TEMPLATE_AS_INACTIVE.replace(
          '{tenant_id}',
          tenantId,
        ).replace('{template_id}', templateId),
      {},
      { headers: this.httpOptions.headers }
    ).pipe(
      map((res: any) => res),
      catchError((error: any) => {
        console.error('Mark Template as Inactive API request failed:', error);
        return throwError(() => new AppErrors(error));
      })
    );
  }
}
